import { multiSelectStyles } from "../styles/shared/multiSelectStyles";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import CompanyOptionLabel from "./CompanyOptionLabel";
import { Autocomplete, Box, Chip, CircularProgress, TextField } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  cleanAutocompleteText,
  dedupeAutocompleteStrings,
  normalizeAutocompleteText } from "../utils/autocompleteOptions";

const cleanValue = (value) => cleanAutocompleteText(value);
const normalizeValue = (value) => normalizeAutocompleteText(value);
const defaultFilterOptions = createFilterOptions();
const DEFAULT_FREE_SOLO_COMMIT_KEYS = ["Enter", "Tab", "|"];
const splitCommittedValues = (
  value,
  { splitFreeSoloOnPipe = true, splitFreeSoloOnComma = false } = {}
) => {
  const cleaned = cleanValue(value);
  if (!cleaned) return [];
  if (!splitFreeSoloOnPipe && !splitFreeSoloOnComma) return [cleaned];

  const delimiters = [
    splitFreeSoloOnPipe ? "\\|" : "",
    splitFreeSoloOnComma ? "," : "",
  ].filter(Boolean);

  if (!delimiters.length) return [cleaned];

  return String(value ?? "")
    .split(new RegExp(`\\s*(?:${delimiters.join("|")})\\s*`, "g"))
    .map((item) => cleanValue(item))
    .filter(Boolean);
};

export default function PopupMultiSelectField({
  placeholder,
  companyJobCounts = false,
  options = [],
  value = [],
  onChange,
  onInputValueChange,
  loading = false,
  freeSolo = true,
  inputSx,
  chipSx,
  chipsBelow = true,
  renderAvatar,
  textFieldProps,
  autocompleteProps,
  splitFreeSoloOnPipe = true,
  splitFreeSoloOnComma = false,
  freeSoloCommitKeys = DEFAULT_FREE_SOLO_COMMIT_KEYS }) {
  const [inputValue, setInputValue] = useState("");
  const [optimisticValue, setOptimisticValue] = useState(null);
  // Autocomplete can fire its blur handler immediately after onChange.  Keep
  // the just-selected values in a ref so that blur never rebuilds the list
  // from the previous render and drops the final selected item.
  const selectedValuesRef = useRef([]);
  const pendingValueRef = useRef(null);
  const commitSelectionChange = useCallback(
    (nextValue = [], nextInputValue = "") => {
      const normalizedNextValue = dedupeAutocompleteStrings(nextValue);
      selectedValuesRef.current = normalizedNextValue;
      pendingValueRef.current = normalizedNextValue;
      const update = () => {
        setOptimisticValue(normalizedNextValue);
        setInputValue(nextInputValue);
        onInputValueChange?.(nextInputValue);
        onChange?.(normalizedNextValue);
      };
      if (freeSolo) flushSync(update);
      else update();
    },
    [freeSolo, onChange, onInputValueChange]
  );

  const normalizedOptions = useMemo(() => dedupeAutocompleteStrings(options), [options]);
  const normalizedValue = useMemo(() => dedupeAutocompleteStrings(value), [value]);
  const valuesMatch = useCallback(
    (left = [], right = []) =>
      left.length === right.length &&
      left.every((item) => right.some((candidate) => normalizeValue(item) === normalizeValue(candidate))),
    []
  );
  const isSubsetOf = useCallback(
    (subset = [], values = []) =>
      subset.every((item) => values.some((candidate) => normalizeValue(item) === normalizeValue(candidate))),
    []
  );
  useEffect(() => {
    const pendingValue = pendingValueRef.current;
    // A parent render from immediately before an option selection can arrive
    // after the selection event.  Do not let that stale, shorter value remove
    // the final chip while the parent catches up with our committed value.
    if (pendingValue) {
      if (valuesMatch(normalizedValue, pendingValue)) {
        pendingValueRef.current = null;
        setOptimisticValue(null);
      } else if (isSubsetOf(normalizedValue, pendingValue)) {
        return;
      } else {
        pendingValueRef.current = null;
        setOptimisticValue(null);
      }
    }
    selectedValuesRef.current = normalizedValue;
  }, [isSubsetOf, normalizedValue, valuesMatch]);
  const displayedValue = optimisticValue || normalizedValue;
  const optionExists = useCallback(
    (candidate) =>
      [...normalizedOptions, ...normalizedValue].some(
        (item) => normalizeValue(item) === normalizeValue(candidate)
      ),
    [normalizedOptions, normalizedValue]
  );
  const filterOptions = useCallback(
    (nextOptions, params) => {
      const filtered = dedupeAutocompleteStrings(defaultFilterOptions(nextOptions, params));
      const nextInput = cleanValue(params?.inputValue);
      if (!freeSolo || !nextInput) return filtered;

      return optionExists(nextInput)
        ? filtered
        : dedupeAutocompleteStrings([...filtered, nextInput]);
    },
    [freeSolo, optionExists]
  );

  useEffect(() => {
    if (!normalizedValue.length && !pendingValueRef.current) {
      selectedValuesRef.current = [];
      setInputValue("");
      onInputValueChange?.("");
    }
  }, [normalizedValue.length, onInputValueChange]);

  const commitFreeSoloValue = useCallback(
    (rawValue) => {
      if (!freeSolo) return false;

      const nextItems = splitCommittedValues(rawValue, {
        splitFreeSoloOnPipe,
        splitFreeSoloOnComma });
      if (!nextItems.length) return false;

      const nextNormalizedValues = [...selectedValuesRef.current];
      let hasChanges = false;

      nextItems.forEach((nextItem) => {
        const exists = nextNormalizedValues.some(
          (item) => normalizeValue(item) === normalizeValue(nextItem)
        );
        if (exists) return;
        nextNormalizedValues.push(nextItem);
        hasChanges = true;
      });

      if (!hasChanges) {
        flushSync(() => {
          setInputValue("");
          onInputValueChange?.("");
        });
        return false;
      }
      commitSelectionChange(nextNormalizedValues);
      return true;
    },
    [
      commitSelectionChange,
      freeSolo,
      onInputValueChange,
      splitFreeSoloOnComma,
      splitFreeSoloOnPipe,
    ]
  );

  const handleDelete = useCallback(
    (target) => {
      commitSelectionChange(
        selectedValuesRef.current.filter((item) => normalizeValue(item) !== normalizeValue(target))
      );
    },
    [commitSelectionChange]
  );
  const shouldKeepDelimiterInInput = useCallback(
    (key) =>
      (key === "," && !splitFreeSoloOnComma) || (key === "|" && !splitFreeSoloOnPipe),
    [splitFreeSoloOnComma, splitFreeSoloOnPipe]
  );

  return (
    <Box sx={multiSelectStyles.root()}>
      {!!displayedValue.length && (
        <Box sx={multiSelectStyles.chips({ chipsBelow })}>
          {displayedValue.map((item, index) => (
            <Chip data-popup-selection-chip
              key={`${item}-${index}`}
              label={item}
              avatar={renderAvatar?.(item)}
              onDelete={() => handleDelete(item)}
              sx={multiSelectStyles.chip({ chipSx })}
            />
          ))}
        </Box>
      )}
      <Autocomplete
        multiple
        freeSolo={freeSolo}
        disableCloseOnSelect
        openOnFocus
        filterSelectedOptions={false}
        selectOnFocus
        handleHomeEndKeys
        includeInputInList={freeSolo}
        clearOnBlur={false}
        options={normalizedOptions}
        value={displayedValue}
        filterOptions={filterOptions}
        getOptionLabel={(option) => cleanValue(option)}
        inputValue={inputValue}
        onInputChange={(_, nextInput, reason) => {
          if (reason === "reset" || reason === "clear") {
            setInputValue("");
            onInputValueChange?.("");
            return;
          }
          setInputValue(nextInput);
          onInputValueChange?.(nextInput);
        }}
        onChange={(_, nextValue, reason) => {
          // MUI may emit a blur change after the selection event with the
          // previous controlled value.  The input blur below commits any
          // free-solo text from the ref, so this event must not replace the
          // already committed selection list.
          if (reason === "blur") return;
          commitSelectionChange(nextValue);
        }}
        renderOption={(props, option, { selected }) => {
          const normalizedOption = cleanValue(option);
          const shouldRenderCreateLabel =
            freeSolo &&
            normalizedOption &&
            normalizeValue(normalizedOption) === normalizeValue(inputValue) &&
            !optionExists(normalizedOption);

          return (
            <Box
              component="li"
              {...props}
              sx={multiSelectStyles.option()}
            >
              <Box component="span" sx={multiSelectStyles.optionContent()}>
                {renderAvatar?.(normalizedOption)}
                {shouldRenderCreateLabel ? `Add "${normalizedOption}"` : companyJobCounts ? <CompanyOptionLabel company={normalizedOption} /> : normalizedOption}
              </Box>
              {selected ? (
                <Box component="span" aria-label="Selected" sx={multiSelectStyles.selectedMark()}>
                  ✓
                </Box>
              ) : null}
            </Box>
          );
        }}
        renderTags={() => null}
        isOptionEqualToValue={(option, selected) => normalizeValue(option) === normalizeValue(selected)}
        noOptionsText={
          autocompleteProps?.noOptionsText ??
          (freeSolo ? "Type to add a new entry" : "No options")
        }
        renderInput={(params) => {
          const extraInputProps = textFieldProps?.InputProps || {};
          const handleBlur = textFieldProps?.onBlur;
          const handleKeyDown = textFieldProps?.onKeyDown;

          return (
            <TextField
              {...params}
              {...textFieldProps}
              size={textFieldProps?.size || "small"}
              placeholder={placeholder}
              sx={multiSelectStyles.input({ inputSx, textFieldProps })}
              onBlur={(event) => {
                // Commit before MUI processes the native blur, which prevents
                // autoSelect/freeSolo blur handling from racing a new chip.
                commitFreeSoloValue(inputValue);
                params.inputProps?.onBlur?.(event);
                handleBlur?.(event);
              }}
              onKeyDown={(event) => {
                handleKeyDown?.(event);
                if (!freeSolo) return;
                if (event.defaultPrevented) return;

                if (shouldKeepDelimiterInInput(event.key)) {
                  event.stopPropagation();
                  return;
                }

                if (
                  freeSoloCommitKeys.includes(event.key) &&
                  inputValue.trim()
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  commitFreeSoloValue(inputValue);
                }
              }}
              InputProps={{
                ...params.InputProps,
                ...extraInputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {extraInputProps.endAdornment}
                    {params.InputProps.endAdornment}
                  </>
                ) }}
            />
          );
        }}
        {...autocompleteProps}
        slotProps={{
          ...autocompleteProps?.slotProps,
          popper: {
            ...autocompleteProps?.slotProps?.popper,
            placement: "bottom-start",
            modifiers: [
              ...(autocompleteProps?.slotProps?.popper?.modifiers || []),
              { name: "flip", enabled: false },
            ] } }}
      />
    </Box>
  );
}

export { popupFilterInputSx, popupFilterChipSx } from "../styles/shared/multiSelectStyles";
